import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  automationService,
  type Strategy,
  type PositionSizing,
  type ExitRules,
  type StopRule,
  type TrailingStopRule,
  type RuleConfig,
  type ForexRuleConfig,
} from '@/services/strategyService';
import { paperAccountService, type PaperAccount } from '@/services/paperAccountService';
import { scannerApiService } from '@/services/scannerService';
import { Save, X } from 'lucide-react';

interface RuleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pass existing rule to edit; null = create new */
  editRule?: Strategy | null;
  onSaved: () => void;
}

interface ScannerOption {
  id: string;
  name: string;
  assetClass: 'Equity' | 'Forex' | 'Crypto';
}

const STOP_MODES = ['Dollar', 'Percent', 'AtrMultiple', 'RMultiple'] as const;
const SIZING_MODES = ['FixedShares', 'FixedDollar', 'RiskPercent', 'PercentEquity'] as const;

const defaultSizing: PositionSizing = {
  mode: 'RiskPercent',
  value: 2.0,
  accountEquity: 100000,
  maxNotional: 25000,
};

const defaultExits: ExitRules = {
  stopLoss: { mode: 'AtrMultiple', value: 1.5 },
  takeProfit: null,
  trailing: { mode: 'Percent', value: 1.5, activationPercent: 0, distanceReference: 'LastPrice' },
  breakEvenAfterRMultiple: 1.0,
  maxHoldMinutes: 240,
};

export const RuleFormDialog: React.FC<RuleFormDialogProps> = ({
  open,
  onOpenChange,
  editRule,
  onSaved,
}) => {
  const [scanners, setScanners] = useState<ScannerOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scannerId, setScannerId] = useState('');
  const [direction, setDirection] = useState<'Long' | 'Short'>('Long');
  const [assetClass, setAssetClass] = useState<'Equity' | 'Forex' | 'Crypto'>('Equity');
  // '' means the shared per-user paper account. Sent verbatim, because the server reads ''
  // as an explicit unbind and null/undefined as "keep the existing binding".
  const [paperAccountId, setPaperAccountId] = useState<string>('');
  const [paperAccounts, setPaperAccounts] = useState<PaperAccount[]>([]);
  // Forex-only config (ForexRuleConfig). Pips are nullable (blank ⇒ ATR fallback).
  const [lotSize, setLotSize] = useState(0.01);
  const [slPips, setSlPips] = useState<number | null>(null);
  const [tpPips, setTpPips] = useState<number | null>(null);
  const [liquidSessionsOnly, setLiquidSessionsOnly] = useState(true);
  const [rolloverBlackout, setRolloverBlackout] = useState(true);
  const [sizing, setSizing] = useState<PositionSizing>({ ...defaultSizing });
  const [exits, setExits] = useState<ExitRules>({ ...defaultExits });
  const [cooldownSeconds, setCooldownSeconds] = useState(300);
  const [maxConcurrentTrades, setMaxConcurrentTrades] = useState(5);
  // null = use the server default (60s). Blank input ⇒ null.
  const [entryFillTimeoutSeconds, setEntryFillTimeoutSeconds] = useState<number | null>(null);
  // Market (default) or Limit entry execution; offset is the marketable buffer (bps).
  const [entryOrderType, setEntryOrderType] = useState<'Market' | 'Limit'>('Market');
  const [entryLimitOffsetBps, setEntryLimitOffsetBps] = useState(10);
  // Adverse slippage (bps/side) the backtest models on market fills.
  const [backtestSlippageBps, setBacktestSlippageBps] = useState(5);
  // Entry extension guard: reject if price is > this % above the 50-day SMA. 0 = off.
  const [entryMaxExtAboveSma50Pct, setEntryMaxExtAboveSma50Pct] = useState(15);
  const [regularHoursOnly, setRegularHoursOnly] = useState(true);

  // TP toggle (since it's nullable)
  const [tpEnabled, setTpEnabled] = useState(false);
  const [trailingEnabled, setTrailingEnabled] = useState(true);

  useEffect(() => {
    if (!open) return;
    scannerApiService.getScanners().then((list: any[]) => {
      setScanners(list.map(s => ({ id: s.id, name: s.name, assetClass: s.assetClass === 'Forex' ? 'Forex' : s.assetClass === 'Crypto' ? 'Crypto' : 'Equity' })));
    }).catch(() => {});
    // Accounts are optional context — a failure here must not block editing the strategy,
    // so the picker just falls back to "shared account only".
    paperAccountService.list().then(setPaperAccounts).catch(() => setPaperAccounts([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (editRule) {
      setName(editRule.name);
      setDescription(editRule.description);
      setScannerId(editRule.scannerId);
      setDirection(editRule.direction);
      setSizing({ ...editRule.sizing });
      setExits({ ...editRule.exits });
      setCooldownSeconds(editRule.cooldownSeconds);
      setMaxConcurrentTrades(editRule.maxConcurrentTrades);
      setEntryFillTimeoutSeconds(editRule.entryFillTimeoutSeconds ?? null);
      setEntryOrderType(editRule.entryOrderType ?? 'Market');
      setEntryLimitOffsetBps(editRule.entryLimitOffsetBps ?? 10);
      setBacktestSlippageBps(editRule.backtestSlippageBps ?? 5);
      setEntryMaxExtAboveSma50Pct(editRule.entryMaxExtensionAboveSma50Pct ?? 15);
      setRegularHoursOnly(editRule.regularHoursOnly);
      setTpEnabled(editRule.exits.takeProfit != null);
      setTrailingEnabled(editRule.exits.trailing != null);
      const ac = (editRule.assetClass === 'Forex' ? 'Forex' : editRule.assetClass === 'Crypto' ? 'Crypto' : 'Equity');
      setAssetClass(ac);
      setPaperAccountId(editRule.paperAccountId ?? '');
      const fx = editRule.ruleConfig?.$type === 'Forex' ? editRule.ruleConfig as ForexRuleConfig : null;
      setLotSize(fx?.lotSize ?? 0.01);
      setSlPips(fx?.stopLossPips ?? null);
      setTpPips(fx?.takeProfitPips ?? null);
      setLiquidSessionsOnly(fx?.liquidSessionsOnly ?? true);
      setRolloverBlackout(fx ? fx.rolloverBlackoutStartEt != null : true);
    } else {
      setName('');
      setDescription('');
      setScannerId('');
      setDirection('Long');
      setSizing({ ...defaultSizing });
      setExits({ ...defaultExits });
      setCooldownSeconds(300);
      setMaxConcurrentTrades(5);
      setEntryFillTimeoutSeconds(null);
      setEntryOrderType('Market');
      setEntryLimitOffsetBps(10);
      setBacktestSlippageBps(5);
      setEntryMaxExtAboveSma50Pct(15);
      setRegularHoursOnly(true);
      setTpEnabled(false);
      setTrailingEnabled(true);
      setAssetClass('Equity');
      setPaperAccountId('');
      setLotSize(0.01);
      setSlPips(null);
      setTpPips(null);
      setLiquidSessionsOnly(true);
      setRolloverBlackout(true);
    }
    setError(null);
  }, [open, editRule]);

  const boundAccount = paperAccounts.find(a => a.accountId === paperAccountId) ?? null;

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    if (!scannerId) { setError('Scanner is required'); return; }

    const isForex = assetClass === 'Forex';
    if (isForex && lotSize <= 0) { setError('Lot size must be greater than 0'); return; }

    setSaving(true);
    setError(null);
    try {
      const ruleConfig: RuleConfig = isForex
        ? {
            $type: 'Forex',
            lotSize,
            stopLossPips: slPips,
            takeProfitPips: tpPips,
            liquidSessionsOnly,
            rolloverBlackoutStartEt: rolloverBlackout ? '17:00:00' : null,
            rolloverBlackoutDuration: '00:05:00',
          }
        : { $type: 'Equity' };

      const payload: Partial<Strategy> = {
        // '' is the explicit unbind signal — the server preserves the existing binding on
        // null/undefined so a client that omits the field can't silently unbind a strategy.
        paperAccountId: paperAccountId,
        name: name.trim(),
        description: description.trim(),
        scannerId,
        direction,
        assetClass,
        ruleConfig,
        sizing,
        exits: {
          ...exits,
          // Forex drives take-profit via pips on ruleConfig — keep the root TP off
          // so the evaluator uses the pip target (or none) rather than double-setting.
          takeProfit: isForex ? null : (tpEnabled ? (exits.takeProfit ?? { mode: 'RMultiple', value: 2.0 }) : null),
          trailing: trailingEnabled ? (exits.trailing ?? { mode: 'Percent', value: 1.5, activationPercent: 0, distanceReference: 'LastPrice' }) : null,
        },
        cooldownSeconds,
        maxConcurrentTrades,
        entryFillTimeoutSeconds,
        entryOrderType,
        entryLimitOffsetBps,
        backtestSlippageBps,
        entryMaxExtensionAboveSma50Pct: entryMaxExtAboveSma50Pct,
        // Regular-hours gating is equity-only; forex trades 24/5 with its own
        // liquid-session gate (on ruleConfig).
        regularHoursOnly: isForex ? false : regularHoursOnly,
        enabled: editRule?.enabled ?? false,
        // updateRule REPLACES the document, so any trade-affecting field this form doesn't render
        // has to be carried through explicitly or an unrelated edit silently clears it — which for
        // orbEntry would turn an ORB strategy back into a scanner-edge one without a word.
        ...(editRule ? {
          orbEntry: editRule.orbEntry ?? null,
          maxEntriesPerSymbolPerDay: editRule.maxEntriesPerSymbolPerDay ?? 0,
          backtestIntrabarExits: editRule.backtestIntrabarExits ?? true,
          minStopDistancePct: editRule.minStopDistancePct ?? 0,
          backtestCommissionPerUnit: editRule.backtestCommissionPerUnit ?? 0,
          backtestCommissionPerTrade: editRule.backtestCommissionPerTrade ?? 0,
          // entryFilters has never been part of this payload, so every save through this form
          // silently cleared a strategy's entry filters. Carrying it through is what makes an
          // SMA-alignment gate (DistFromSma20Pct) survive an unrelated edit.
          entryFilters: editRule.entryFilters ?? [],
        } : {}),
      };

      if (editRule) {
        await automationService.updateRule(editRule.id, payload);
      } else {
        await automationService.createRule(payload);
      }
      onSaved();
      onOpenChange(false);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const updateStopLoss = (patch: Partial<StopRule>) => {
    setExits(prev => ({
      ...prev,
      stopLoss: { ...(prev.stopLoss ?? { mode: 'AtrMultiple', value: 1.5 }), ...patch } as StopRule,
    }));
  };

  const updateTakeProfit = (patch: Partial<StopRule>) => {
    setExits(prev => ({
      ...prev,
      takeProfit: { ...(prev.takeProfit ?? { mode: 'RMultiple', value: 2.0 }), ...patch } as StopRule,
    }));
  };

  const updateTrailing = (patch: Partial<TrailingStopRule>) => {
    setExits(prev => ({
      ...prev,
      trailing: {
        ...(prev.trailing ?? { mode: 'Percent', value: 1.5, activationPercent: 0, distanceReference: 'LastPrice' }),
        ...patch,
      } as TrailingStopRule,
    }));
  };

  const isForex = assetClass === 'Forex';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editRule ? 'Edit Rule' : 'New Automation Rule'}</DialogTitle>
          <DialogDescription>
            Configure scanner trigger, position sizing, and exit strategy
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {error && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">{error}</div>
          )}

          {/* ── Basic Info ── */}
          <Section title="Basic">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Name" className="col-span-full">
                <input
                  className="input-field"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="My Trading Rule"
                />
              </Field>
              <Field label="Description" className="col-span-full">
                <input
                  className="input-field"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Enter on scanner hit, ATR stop, trailing exit"
                />
              </Field>
              <Field label="Asset Class" className="col-span-full">
                <select
                  className="input-field"
                  value={assetClass}
                  onChange={e => {
                    const next = e.target.value as 'Equity' | 'Forex' | 'Crypto';
                    setAssetClass(next);
                    // Clear a now-mismatched scanner so the save payload can't carry an
                    // equity scanner on a forex rule (and vice versa) (#161 review).
                    const sel = scanners.find(s => s.id === scannerId);
                    if (sel && sel.assetClass !== next) setScannerId('');
                  }}
                >
                  <option value="Equity">Equity — shares, ATR/$ stops, market hours</option>
                  <option value="Forex">Forex (FXCM) — lots, pip stops, 24/5 sessions</option>
                  <option value="Crypto">Crypto — units, ATR/$ stops, 24/7</option>
                </select>
              </Field>
              <Field label="Scanner">
                <select
                  className="input-field"
                  value={scannerId}
                  onChange={e => setScannerId(e.target.value)}
                >
                  <option value="">Select scanner...</option>
                  {scanners
                    // Show scanners matching this rule's asset class (legacy untagged
                    // scanners read as Equity). Always keep the currently-selected one
                    // visible so editing a rule never silently drops its scanner.
                    .filter(s => s.assetClass === assetClass || s.id === scannerId)
                    .map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                </select>
              </Field>
              <Field label="Direction">
                <select
                  className="input-field"
                  value={direction}
                  onChange={e => setDirection(e.target.value as 'Long' | 'Short')}
                >
                  <option value="Long">Long</option>
                  <option value="Short">Short</option>
                </select>
              </Field>
            </div>
          </Section>

          {/* ── Position Sizing ── */}
          <Section title="Position Sizing">
            {isForex ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Lot Size">
                  <input
                    type="number"
                    className="input-field"
                    value={lotSize}
                    onChange={e => setLotSize(Number(e.target.value))}
                    step={0.01}
                    min={0.01}
                  />
                </Field>
                <div className="flex items-end text-xs text-muted-foreground pb-2">
                  1.0 = standard (100K), 0.1 = mini, 0.01 = micro
                </div>
              </div>
            ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Sizing Mode">
                <select
                  className="input-field"
                  value={sizing.mode}
                  onChange={e => setSizing(prev => ({ ...prev, mode: e.target.value as any }))}
                >
                  {SIZING_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <Field label={sizing.mode === 'RiskPercent' ? 'Risk %' : sizing.mode === 'PercentEquity' ? 'Equity %' : 'Value'}>
                <input
                  type="number"
                  className="input-field"
                  value={sizing.value}
                  onChange={e => setSizing(prev => ({ ...prev, value: Number(e.target.value) }))}
                  step={sizing.mode.includes('Percent') ? 0.5 : 1}
                />
              </Field>
              <Field label="Account Equity ($)">
                <input
                  type="number"
                  className="input-field"
                  value={sizing.accountEquity}
                  onChange={e => setSizing(prev => ({ ...prev, accountEquity: Number(e.target.value) }))}
                />
              </Field>
              <Field label="Max Notional ($)">
                <input
                  type="number"
                  className="input-field"
                  value={sizing.maxNotional}
                  onChange={e => setSizing(prev => ({ ...prev, maxNotional: Number(e.target.value) }))}
                />
              </Field>
            </div>
            )}

            {/* Binding a strategy to its own pot of capital is what stops two strategies
                over-committing one balance — and blocking each other when one hits a
                buying-power limit. The account's capital overrides Account Equity above. */}
            <div className="mt-3">
              <Field label="Paper Account">
                <select
                  className="input-field"
                  value={paperAccountId ?? ''}
                  onChange={e => setPaperAccountId(e.target.value)}
                >
                  <option value="">Shared paper account</option>
                  {paperAccounts
                    .filter(a => a.assetClass === assetClass)
                    .map(a => (
                      <option key={a.accountId} value={a.accountId}>
                        {a.name} — ${a.startingEquity.toLocaleString()}{a.enabled ? '' : ' (disabled)'}
                      </option>
                    ))}
                </select>
              </Field>
              <div className="text-xs text-muted-foreground mt-1">
                {boundAccount
                  ? `Positions size off this account's $${boundAccount.startingEquity.toLocaleString()}, not Account Equity above.`
                  : 'Shares one balance and one buying-power circuit breaker with every other unbound strategy.'}
              </div>
            </div>
          </Section>

          {/* ── Exit Rules ── */}
          <Section title="Exit Rules">
            <div className="space-y-3">
              {/* Stop Loss + Take Profit — forex expresses these in pips; equity in
                  mode/value. Trailing/break-even/time below are shared (price math). */}
              {isForex ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                  <Field label="Stop Loss (pips)">
                    <input
                      type="number"
                      className="input-field"
                      value={slPips ?? ''}
                      onChange={e => { const v = e.target.value.trim(); setSlPips(v === '' ? null : Number(v)); }}
                      step={1}
                      min={0}
                      placeholder="blank = ATR stop"
                      title="Stop distance in pips. Blank falls back to the ATR-based stop."
                    />
                  </Field>
                  <Field label="Take Profit (pips)">
                    <input
                      type="number"
                      className="input-field"
                      value={tpPips ?? ''}
                      onChange={e => { const v = e.target.value.trim(); setTpPips(v === '' ? null : Number(v)); }}
                      step={1}
                      min={0}
                      placeholder="blank = none"
                    />
                  </Field>
                </div>
              ) : (
              <>
              {/* Stop Loss */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                <Field label="Stop Loss Mode">
                  <select
                    className="input-field"
                    value={exits.stopLoss?.mode ?? 'AtrMultiple'}
                    onChange={e => updateStopLoss({ mode: e.target.value as any })}
                  >
                    {STOP_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </Field>
                <Field label="Stop Value">
                  <input
                    type="number"
                    className="input-field"
                    value={exits.stopLoss?.value ?? 1.5}
                    onChange={e => updateStopLoss({ value: Number(e.target.value) })}
                    step={0.1}
                  />
                </Field>
                <div />
              </div>

              {/* Take Profit */}
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  id="tp-toggle"
                  checked={tpEnabled}
                  onChange={e => setTpEnabled(e.target.checked)}
                  className="rounded border"
                />
                <label htmlFor="tp-toggle" className="text-xs font-medium cursor-pointer">Enable Take Profit</label>
              </div>
              {tpEnabled && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                  <Field label="TP Mode">
                    <select
                      className="input-field"
                      value={exits.takeProfit?.mode ?? 'RMultiple'}
                      onChange={e => updateTakeProfit({ mode: e.target.value as any })}
                    >
                      {STOP_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </Field>
                  <Field label="TP Value">
                    <input
                      type="number"
                      className="input-field"
                      value={exits.takeProfit?.value ?? 2.0}
                      onChange={e => updateTakeProfit({ value: Number(e.target.value) })}
                      step={0.1}
                    />
                  </Field>
                  <div />
                </div>
              )}
              </>
              )}

              {/* Trailing Stop */}
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  id="trail-toggle"
                  checked={trailingEnabled}
                  onChange={e => setTrailingEnabled(e.target.checked)}
                  className="rounded border"
                />
                <label htmlFor="trail-toggle" className="text-xs font-medium cursor-pointer">Enable Trailing Stop</label>
              </div>
              {trailingEnabled && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                    <Field label="Trail Mode">
                      <select
                        className="input-field"
                        value={exits.trailing?.mode ?? 'Percent'}
                        onChange={e => updateTrailing({ mode: e.target.value as any })}
                      >
                        {STOP_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </Field>
                    <Field label="Trail Value">
                      <input
                        type="number"
                        className="input-field"
                        value={exits.trailing?.value ?? 1.5}
                        onChange={e => updateTrailing({ value: Number(e.target.value) })}
                        step={0.1}
                      />
                    </Field>
                    <Field label="Activation %">
                      <input
                        type="number"
                        className="input-field"
                        value={exits.trailing?.activationPercent ?? 0}
                        onChange={e => updateTrailing({ activationPercent: Number(e.target.value) })}
                        step={0.5}
                      />
                    </Field>
                  </div>
                  {/* Distance reference is only meaningful for Percent mode — the other
                      modes use a reference-independent distance (Dollar / ATR / R-multiple).
                      Hide it entirely when not Percent so the form doesn't suggest a
                      setting that has no effect. */}
                  {exits.trailing?.mode === 'Percent' && (
                    <div className="grid grid-cols-1 gap-3 mt-2">
                      <Field label="Trail Distance Reference">
                        <select
                          className="input-field"
                          value={exits.trailing?.distanceReference ?? 'LastPrice'}
                          onChange={e => updateTrailing({ distanceReference: e.target.value as any })}
                        >
                          <option value="LastPrice">Last Price (legacy) — tightens on retrace</option>
                          <option value="MaxFavorable">Max Favorable (classic) — % behind the high</option>
                        </select>
                      </Field>
                    </div>
                  )}
                </>
              )}

              {/* Break-even & Time */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                <Field label="Break-Even After R-Multiple">
                  <input
                    type="number"
                    className="input-field"
                    value={exits.breakEvenAfterRMultiple}
                    onChange={e => setExits(prev => ({ ...prev, breakEvenAfterRMultiple: Number(e.target.value) }))}
                    step={0.5}
                  />
                </Field>
                <Field label="Max Hold Minutes (0 = no limit)">
                  <input
                    type="number"
                    className="input-field"
                    value={exits.maxHoldMinutes}
                    onChange={e => setExits(prev => ({ ...prev, maxHoldMinutes: Number(e.target.value) }))}
                    step={30}
                  />
                </Field>
              </div>
            </div>
          </Section>

          {/* ── Controls ── */}
          <Section title="Controls">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Cooldown (seconds)">
                <input
                  type="number"
                  className="input-field"
                  value={cooldownSeconds}
                  onChange={e => setCooldownSeconds(Number(e.target.value))}
                  min={0}
                  step={60}
                />
              </Field>
              <Field label="Max Concurrent Trades">
                <input
                  type="number"
                  className="input-field"
                  value={maxConcurrentTrades}
                  onChange={e => setMaxConcurrentTrades(Number(e.target.value))}
                  min={1}
                  max={50}
                />
              </Field>
              <Field label="Entry Fill Timeout (sec)">
                <input
                  type="number"
                  className="input-field"
                  value={entryFillTimeoutSeconds ?? ''}
                  onChange={e => {
                    const v = e.target.value.trim();
                    const n = Number(v);
                    // Blank or non-finite (transient input like "-"/"1e") ⇒ null ⇒ server default.
                    setEntryFillTimeoutSeconds(v === '' || !Number.isFinite(n) ? null : n);
                  }}
                  min={1}
                  max={3600}
                  placeholder="60 (default)"
                  title="Cancel an entry order left unfilled this long as illiquid. Blank = 60s default."
                />
              </Field>
              <Field label="Entry Order Type">
                <select
                  className="input-field"
                  value={entryOrderType}
                  onChange={e => setEntryOrderType(e.target.value as 'Market' | 'Limit')}
                  title="Market fills immediately (pays the spread + slippage). Limit fills only at/through the signal price, capping slippage; a fast move past it cancels via the fill timeout."
                >
                  <option value="Market">Market</option>
                  <option value="Limit">Limit (cap slippage)</option>
                </select>
              </Field>
              {entryOrderType === 'Limit' && (
                <Field label="Limit Offset (bps)">
                  <input
                    type="number"
                    className="input-field"
                    value={entryLimitOffsetBps}
                    onChange={e => setEntryLimitOffsetBps(Math.max(0, Number(e.target.value) || 0))}
                    min={0}
                    max={500}
                    title="Marketable buffer over the signal price: buy fills up to price×(1+bps), sell down to price×(1−bps). 10 bps = 0.10%."
                  />
                </Field>
              )}
              <Field label="Backtest Slippage (bps)">
                <input
                  type="number"
                  className="input-field"
                  value={backtestSlippageBps}
                  onChange={e => setBacktestSlippageBps(Math.max(0, Number(e.target.value) || 0))}
                  min={0}
                  max={1000}
                  title="Adverse slippage the backtest applies to market fills so simulated PnL reflects real cost. Limit entries are exempt. 5 bps default; intraday thin names ran ~17 bps live."
                />
              </Field>
              <Field label="Max Ext. Above SMA50 (%)">
                <input
                  type="number"
                  className="input-field"
                  value={entryMaxExtAboveSma50Pct}
                  onChange={e => setEntryMaxExtAboveSma50Pct(Math.max(0, Number(e.target.value) || 0))}
                  min={0}
                  max={1000}
                  title="Entry guard: reject if price is more than this % above the 50-day SMA (don't chase a name that's already run; for shorts it's % below). 0 = off. No effect on intraday scanners without a daily SMA. Note: if your scanner already filters DistFromSma50Pct, the tighter of the two wins. Default 15."
                />
              </Field>
              {isForex ? (
                <>
                  <Field label="">
                    <div className="flex items-center gap-2 h-9">
                      <input
                        type="checkbox"
                        id="liquid-toggle"
                        checked={liquidSessionsOnly}
                        onChange={e => setLiquidSessionsOnly(e.target.checked)}
                        className="rounded border"
                      />
                      <label htmlFor="liquid-toggle" className="text-xs font-medium cursor-pointer" title="Only enter during the London+NY overlap (08:00–17:00 ET).">Liquid Sessions Only</label>
                    </div>
                  </Field>
                  <Field label="">
                    <div className="flex items-center gap-2 h-9">
                      <input
                        type="checkbox"
                        id="rollover-toggle"
                        checked={rolloverBlackout}
                        onChange={e => setRolloverBlackout(e.target.checked)}
                        className="rounded border"
                      />
                      <label htmlFor="rollover-toggle" className="text-xs font-medium cursor-pointer" title="Skip entries around the 17:00 ET swap-roll window (wide spreads).">Avoid Rollover (17:00 ET)</label>
                    </div>
                  </Field>
                </>
              ) : assetClass === 'Crypto' ? (
              <Field label="">
                <div className="flex items-center h-9 text-xs text-muted-foreground">Crypto trades 24/7 — no session gate.</div>
              </Field>
              ) : (
              <Field label="">
                <div className="flex items-center gap-2 h-9">
                  <input
                    type="checkbox"
                    id="rth-toggle"
                    checked={regularHoursOnly}
                    onChange={e => setRegularHoursOnly(e.target.checked)}
                    className="rounded border"
                  />
                  <label htmlFor="rth-toggle" className="text-xs font-medium cursor-pointer">Regular Hours Only</label>
                </div>
              </Field>
              )}
            </div>
          </Section>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-3.5 w-3.5 mr-1" /> Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving...' : editRule ? 'Update Rule' : 'Create Rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Small helpers ──

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Card>
    <CardContent className="p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{title}</div>
      {children}
    </CardContent>
  </Card>
);

const Field: React.FC<{ label: string; className?: string; children: React.ReactNode }> = ({
  label,
  className,
  children,
}) => (
  <div className={className}>
    {label && <label className="text-xs text-muted-foreground block mb-1">{label}</label>}
    {children}
  </div>
);
